import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Edit, ChevronLeft, ChevronRight, Search, Plane, Move, Clock, Copy, HardHat, Book, Maximize2, Minimize2, Mail } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import UserDayTravelView from "./UserDayTravelView";
import CopyTravelDialog from "./CopyTravelDialog";
import EditTravelDialog from "./EditTravelDialog";
import { useOutletContext } from "react-router-dom";
import AddOrEditUserForm from './../company/UserCreator';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

const ItemTypes = {
  TRAVEL: "travel",
};

const extractUserId = (userString) => {
  if (!userString) return null;
  const matches = userString.match(/\(([^)]+)\)/);
  return matches && matches[1] ? matches[1] : null;
};

const TravelItem = ({ item, userId, date, onDoubleClick, onEditTravel, onCopyTravel, isDraggable = true }) => {
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: ItemTypes.TRAVEL,
    item: () => ({
      id: item.id,
      type: "travel",
      travelData: item,
      sourceUserId: userId,
      sourceDate: date,
    }),
    canDrag: isDraggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [item, userId, date, isDraggable]);

  return (
    <div
      ref={preview}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick(item);
      }}
      className={`p-1 rounded text-[9px] border shadow-sm bg-green-200 border-green-400 hover:shadow-md transition-shadow relative overflow-hidden ${isDragging ? "opacity-50" : "opacity-100"}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"></div>
      <div className="pl-1">
        <div className="font-medium text-[9px] mb-0.5 flex items-center justify-between">
          <div className="flex items-center overflow-hidden">
            <Plane className="h-2 w-2 mr-0.5 flex-shrink-0 text-green-600" />
            <span className="truncate">Travel</span>
          </div>
          {isDraggable && (
            <div className="flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyTravel(item);
                }}
                className="ml-0.5 p-0.5 rounded hover:bg-green-300 text-green-700"
                title="Copy to another user"
              >
                <Copy className="h-2 w-2" />
              </button>
              <button
                ref={drag}
                className="ml-0.5 p-0.5 rounded hover:bg-green-300 text-green-700 cursor-move"
              >
                <Move className="h-2 w-2" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditTravel(item);
                }}
                className="ml-0.5 p-0.5 rounded hover:bg-green-300 text-green-700"
              >
                <Edit className="h-2 w-2" />
              </button>
            </div>
          )}
        </div>
        <div className="text-[8px] text-gray-500 truncate">{item.location || "No location"}</div>
        <div className="text-[8px] text-gray-500 flex items-center space-x-0.5">
          <Clock className="h-2 w-2 flex-shrink-0" />
          <span>
            {new Date(item.start_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
            {"-"}
            {new Date(item.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
          </span>
        </div>
        <div className={`text-[7px] flex items-center mt-0.5 ${item.email_sent ? 'text-green-600' : 'text-red-600'}`}>
          <Mail className="h-2 w-2 mr-0.5" />
          <span>{item.email_sent ? 'Email sent' : 'Email not sent'}</span>
        </div>
      </div>
    </div>
  );
};

const WorkPermitItem = ({ item, onDoubleClick }) => (
  <div
    onDoubleClick={(e) => {
      e.stopPropagation();
      onDoubleClick(item);
    }}
    className="p-1 rounded text-[9px] border shadow-sm bg-yellow-100 border-yellow-300 hover:shadow-md transition-shadow relative overflow-hidden"
  >
    <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-500"></div>
    <div className="pl-1">
      <div className="font-medium text-[9px] mb-0.5 flex items-center">
        <HardHat className="h-2 w-2 mr-0.5 flex-shrink-0 text-yellow-600" />
        <span className="truncate">Work</span>
      </div>
      <div className="text-[8px] text-gray-600 truncate">{item.address || "No address"}</div>
      <div className="text-[8px] text-gray-500 flex items-center space-x-0.5">
        <Clock className="h-2 w-2 flex-shrink-0" />
        <span>
          {new Date(item.start_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
          {"-"}
          {new Date(item.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
        </span>
      </div>
    </div>
  </div>
);

const TrainingItem = ({ item, onDoubleClick }) => (
  <div
    onDoubleClick={(e) => {
      e.stopPropagation();
      onDoubleClick(item);
    }}
    className="p-1 rounded text-[9px] border shadow-sm bg-blue-100 border-blue-300 hover:shadow-md transition-shadow relative overflow-hidden"
  >
    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
    <div className="pl-1">
      <div className="font-medium text-[9px] mb-0.5 flex items-center">
        <Book className="h-2 w-2 mr-0.5 flex-shrink-0 text-blue-600" />
        <span className="truncate">Trening</span>
      </div>
      <div className="text-[8px] text-gray-600 truncate">{item.address || "No address"}</div>
      <div className="text-[8px] text-gray-500 flex items-center space-x-0.5">
        <Clock className="h-2 w-2 flex-shrink-0" />
        <span>
          {new Date(item.start_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
          {"-"}
          {new Date(item.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
        </span>
      </div>
    </div>
  </div>
);

const TravelCalendarCell = ({ person, day, travelEvents, workPermits, trainings, onDayClick, onOrderDoubleClick, onEditTravel, onMoveTravel, onCopyTravel, calendarView }) => {
  const isToday = day.toDateString() === new Date().toDateString();

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.TRAVEL,
    drop: (item) => {
      onMoveTravel(item, person, day);
      return { moved: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [person, day, onMoveTravel]);

  const dayTravelEvents = travelEvents.filter((item) => {
    if (!item.start_date && !item.planned_date_start) return false;
    const itemDate = new Date(item.start_date || item.planned_date_start);
    return itemDate.toDateString() === day.toDateString() && item.userId === person.username;
  });

  const dayWorkPermits = workPermits.filter((item) => {
    if (!item.start_date) return false;
    const itemDate = new Date(item.start_date);
    return itemDate.toDateString() === day.toDateString() && item.userId === person.username;
  });

  const dayTrainings = trainings.filter((item) => {
    if (!item.start_date) return false;
    const itemDate = new Date(item.start_date);
    return itemDate.toDateString() === day.toDateString() && item.userId === person.username;
  });

  const hasItems = dayTravelEvents.length > 0 || dayWorkPermits.length > 0 || dayTrainings.length > 0;
  const travelCount = dayTravelEvents.length;
  const permitCount = dayWorkPermits.length;
  const trainingCount = dayTrainings.length;
  const dropClass = isOver && canDrop ? "bg-green-300 border-green-500 border-2" : "";

  const getCellHeight = () => {
    if (calendarView === "twomonth" || calendarView === "threemonth") return "min-h-[40px]";
    if (calendarView === "sixmonth") return "min-h-[30px]";
    return "min-h-[60px]";
  };

  const isDetailedView = ["week", "biweek", "month"].includes(calendarView);
  const isCounterView = ["twomonth", "threemonth", "sixmonth"].includes(calendarView);

  return (
    <div
      ref={drop}
      className={`border-b border-r border-gray-200 p-1 ${getCellHeight()} ${hasItems ? (day.getDay() === 0 ? "bg-red-100" : "bg-green-100") : day.getDay() === 0 ? "bg-red-50" : isToday ? "bg-green-50" : "bg-white"} ${dropClass} transition-colors`}
      onClick={() => onDayClick(person, day)}
    >
      {(isCounterView || (isDetailedView && !hasItems)) && (
        <div className="text-[9px] text-gray-500 flex justify-between items-center">
          {hasItems && (
            <div className="flex space-x-1">
              {travelCount > 0 && <span className="px-1 py-0 bg-green-600 text-white rounded-full text-[8px] font-bold">{travelCount}</span>}
              {permitCount > 0 && <span className="px-1 py-0 bg-yellow-600 text-white rounded-full text-[8px] font-bold">{permitCount}</span>}
              {trainingCount > 0 && <span className="px-1 py-0 bg-blue-600 text-white rounded-full text-[8px] font-bold">{trainingCount}</span>}
            </div>
          )}
        </div>
      )}
      {isDetailedView && hasItems && (
        <div className="space-y-1 mt-1">
          {dayTravelEvents.map((item) => (
            <TravelItem
              key={item.id}
              item={item}
              userId={person.username}
              date={day}
              onDoubleClick={(item) => onOrderDoubleClick(person, day, item)}
              onEditTravel={(item) => onEditTravel(person, day, item)}
              onCopyTravel={(item) => onCopyTravel(item)}
            />
          ))}
          {dayWorkPermits.map((item) => (
            <WorkPermitItem
              key={item.id}
              item={item}
              onDoubleClick={(item) => onOrderDoubleClick(person, day, item)}
            />
          ))}
          {dayTrainings.map((item) => (
            <TrainingItem
              key={item.id}
              item={item}
              onDoubleClick={(item) => onOrderDoubleClick(person, day, item)}
            />
          ))}
        </div>
      )}
      {isDetailedView && !hasItems && (
        <div className="flex items-center justify-center h-4 mt-1">
          <div className="text-[8px] text-gray-400">No events</div>
        </div>
      )}
    </div>
  );
};

const TravelPanel = () => {
  const [calendarView, setCalendarView] = useState("biweek");
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - date.getDay());
    return date;
  });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [travelEvents, setTravelEvents] = useState([]);
  const [workPermits, setWorkPermits] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [selectedUserDay, setSelectedUserDay] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showTrainingWithoutTravel, setShowTrainingWithoutTravel] = useState(false);
  const [editingTravel, setEditingTravel] = useState(null);
  const [copyingTravel, setCopyingTravel] = useState(null);
  const [dragIndicator, setDragIndicator] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const companyId = 1;
  const { sidebarCollapsed } = useOutletContext();
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const handleUserSave = () => {
    setIsUserFormOpen(false);
    setEditingUserId(null);
    fetchData(); // Odśwież dane po zapisaniu
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error("Błąd podczas wchodzenia w tryb pełnoekranowy:", err);
        toast.error("Nie udało się włączyć trybu pełnoekranowego");
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error("Błąd podczas wychodzenia z trybu pełnoekranowego:", err);
        toast.error("Nie udało się wyjść z trybu pełnoekranowego");
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        toast.error("Brak tokenu autoryzacji. Zaloguj się ponownie.");
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Błąd podczas pobierania użytkowników:", error);
      toast.error("Błąd ładowania listy użytkowników");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      if (!token) {
        toast.error("Brak tokenu autoryzacji. Zaloguj się ponownie.");
        return;
      }

      const startDate = new Date(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      const daysToShow = {
        week: 7,
        biweek: 14,
        month: 28,
        twomonth: 56,
        threemonth: 84,
        sixmonth: 168,
      }[calendarView] || 7;
      endDate.setDate(startDate.getDate() + daysToShow );

      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
        },
      };

      const [travelResponse, workPermitResponse, trainingResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/travel/`, config),
        axios.get(`${API_BASE_URL}/workpermit/`, config),
        axios.get(`${API_BASE_URL}/trainings/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
          }
        }),
      ]);

      const travelList = travelResponse.data.map(travel => {
        const user = users.find(u => u.id === travel.user_id);
        return {
          id: `travel-${travel.id}`,
          userId: user ? user.username : null,
          user_id: travel.user_id, // Keep original user_id
          address: travel.address,
          description: travel.description,
          start_date: travel.start_date,
          end_date: travel.end_date,
          location: travel.address || "Nieznana lokalizacja",
          type: "travel",
          attachment_ferry: travel.attachment_ferry,
          attachment_flight: travel.attachment_flight,
          email_sent: travel.email_sent,
          email_sent_at: travel.email_sent_at,
          required_items: travel.required_items || "",
        };
      }).filter(travel => travel.userId);

      const workPermitList = workPermitResponse.data.flatMap(permit => {
        const users = permit.team_data?.users || [];
        return users.map(user => ({
          id: `permit-${permit.id}-${user.id}`,
          userId: user.username,
          work_permit_name: permit.work_permit_number || permit.order_number || "Nienazwane pozwolenie",
          start_date: permit.planned_date_start,
          end_date: permit.planned_date_end,
          address: permit.turbine_data?.location,
          type: "workpermit",
        }));
      }).filter(permit => permit.userId && permit.start_date);

      const trainingList = trainingResponse.data.flatMap(training => {
        // Training endpoint returns training with users_data array
        if (!training.users_data || !Array.isArray(training.users_data)) {
          return [];
        }
        
        return training.users_data.map((user, index) => {
          const foundUser = users.find(u => u.id === user.id);
          return {
            id: `training-${training.id}-${user.id}`,
            userId: foundUser ? foundUser.username : null,
            trainingId: training.id,
            trainingName: training.name || training.training_center_qualification_data?.qualification_type_data?.name || "Nienazwane szkolenie",
            start_date: training.start_date,
            end_date: training.end_date,
            address: training.training_center_qualification_data?.training_center?.address || "Brak adresu",
            location: training.training_center_qualification_data?.training_center?.name || "Nieznane centrum",
            description: training.description || "",
            training_center: training.training_center_qualification_data?.training_center,
            required_items: training.required_items || "",
            type: "training",
          };
        });
      }).filter(training => training.userId);

      setTravelEvents(travelList);
      setWorkPermits(workPermitList);
      setTrainings(trainingList);
    } catch (error) {
      console.error("Błąd podczas pobierania danych zależnych od dat:", error);
      toast.error("Błąd ładowania danych");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      fetchData();
    }
  }, [currentWeekStart, calendarView, users]);

  const navigateWeek = (direction) => {
    setCurrentWeekStart((prevDate) => {
      const newDate = new Date(prevDate);
      let daysToAdd;
      switch (calendarView) {
        case "biweek": daysToAdd = 14; break;
        case "month": daysToAdd = 28; break;
        case "twomonth": daysToAdd = 56; break;
        case "threemonth": daysToAdd = 84; break;
        case "sixmonth": daysToAdd = 168; break;
        default: daysToAdd = 7;
      }
      newDate.setDate(prevDate.getDate() + (direction === "next" ? daysToAdd : -daysToAdd));
      return newDate;
    });
  };

  const getDaysForView = () => {
    const days = [];
    let daysToShow;
    switch (calendarView) {
      case "biweek": daysToShow = 14; break;
      case "month": daysToShow = 28; break;
      case "twomonth": daysToShow = 56; break;
      case "threemonth": daysToShow = 84; break;
      case "sixmonth": daysToShow = 168; break;
      default: daysToShow = 7;
    }
    for (let i = 0; i < daysToShow; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getColumnWidth = () => {
    const userColumnWidth = 180;
    const margin = 18;
    const sidebarWidth = sidebarCollapsed ? 64 : 256;
    const availableWidth = windowWidth - userColumnWidth - margin - sidebarWidth;
    const daysCount = getDaysForView().length;
    const minWidths = {
      week: 70,
      biweek: 70,
      month: 70,
      twomonth: 60,
      threemonth: 55,
      sixmonth: 50,
    };

    const calculatedWidth = availableWidth / daysCount;
    const minWidth = minWidths[calendarView] || 90;

    return `${Math.max(calculatedWidth, minWidth)}px`;
  };

  const getGridMinWidth = () => {
    const columnWidth = parseFloat(getColumnWidth());
    const daysCount = getDaysForView().length;
    const totalWidth = 180 + columnWidth * daysCount;
    return `${totalWidth}px`;
  };

  const handleDayClick = (person, date) => setSelectedUserDay({ person, date });

  const handleOrderDoubleClick = (person, date, item) => {
    setSelectedUserDay({ person, date });
  };

  const handleEditTravel = (person, date, item) => {
    setEditingTravel({ person, date, item });
  };

  const handleCopyTravel = (item) => {
    setCopyingTravel(item);
  };

  const handleMoveTravel = async (item, targetPerson, targetDate) => {
    try {
      const token = localStorage.getItem("access_token");
      const targetUser = users.find((u) => u.username === targetPerson.username);
      if (!targetUser) {
        toast.error("Target user not found");
        return;
      }

      const travelId = item.id.replace("travel-", "");
      const sourceStartDate = new Date(item.travelData.start_date);
      const sourceEndDate = new Date(item.travelData.end_date);
      const targetStartDate = new Date(targetDate);
      targetStartDate.setHours(sourceStartDate.getHours(), sourceStartDate.getMinutes(), 0, 0);
      const targetEndDate = new Date(targetDate);
      targetEndDate.setHours(sourceEndDate.getHours(), sourceEndDate.getMinutes(), 0, 0);

      const updateData = {
        user: targetUser.id,
        start_date: targetStartDate.toISOString(),
        end_date: targetEndDate.toISOString(),
      };

      setDragIndicator({ userId: targetPerson.username, date: targetDate });

      await axios.patch(
        `${API_BASE_URL}/travel/${travelId}/`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        }
      );

      toast.success("Successfully moved travel");
      fetchData();
    } catch (error) {
      console.error("Error moving travel:", error);
      toast.error("Failed to move travel");
    } finally {
      setDragIndicator(null);
    }
  };

  const formatDateRange = (startDate) => {
    const endDate = new Date(startDate);
    let daysToAdd;
    switch (calendarView) {
      case "biweek": daysToAdd = 13; break;
      case "month": daysToAdd = 27; break;
      case "twomonth": daysToAdd = 55; break;
      case "threemonth": daysToAdd = 83; break;
      case "sixmonth": daysToAdd = 167; break;
      default: daysToAdd = 6;
    }
    endDate.setDate(startDate.getDate() + daysToAdd);
    return `${startDate.toLocaleDateString("en-US", { day: "numeric", month: "long" })} - ${endDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}`;
  };

  const formatDayDisplay = (day, index) => {
    const isToday = day.toDateString() === new Date().toDateString();
    if (calendarView === "threemonth" || calendarView === "sixmonth") {
      return (
        <div className="text-xs font-medium text-gray-700 flex flex-col items-center">
          {day.getDate() === 1 && (
            <span className="text-[7px] text-gray-500 font-bold">{day.toLocaleDateString("en-US", { month: "short" })}</span>
          )}
          <span className={`text-[9px] ${isToday ? "font-bold text-green-600" : ""}`}>{day.getDate()}</span>
        </div>
      );
    } else if (calendarView === "twomonth") {
      return (
        <div className="text-xs font-medium text-gray-700 flex flex-col items-center">
          {day.getDate() === 1 && (
            <span className="text-[8px] text-gray-500 font-bold">{day.toLocaleDateString("en-US", { month: "short" })}</span>
          )}
          <span className={`text-[10px] ${isToday ? "font-bold text-green-600" : ""}`}>{day.getDate()}</span>
        </div>
      );
    } else {
      return (
        <div className="text-xs font-medium text-gray-700 flex flex-col items-center">
          <span className="text-[9px] text-gray-500">{day.toLocaleDateString("en-US", { weekday: "short" })}</span>
          <span className={`text-sm ${isToday ? "font-bold text-green-600" : ""}`}>{day.getDate()}</span>
          <span className="text-[9px] text-gray-500">{day.toLocaleDateString("en-US", { month: "short" })}</span>
        </div>
      );
    }
  };

  const getActiveUsers = () => {
    let filteredUsers = users;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filteredUsers = filteredUsers.filter((user) =>
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter users with training but no travel
    if (showTrainingWithoutTravel) {
      filteredUsers = filteredUsers.filter(user => {
        const userTrainings = trainings.filter(t => t.userId === user.username);
        const userTravels = travelEvents.filter(t => t.userId === user.username);
        return userTrainings.length > 0 && userTravels.length === 0;
      });
    }
    
    return filteredUsers.sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DndProvider backend={HTML5Backend}>
        <div className="min-h-screen bg-white border">
          <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4">
            <div className="flex flex-col space-y-2">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <div className="ml-4 items-start text-2xl font-bold text-[#002D43] ">
                  Travel Panel
                </div>
                <div className="md:flex-1 md:flex md:justify-center">

                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={() => navigateWeek("prev")} className="shadow-sm h-8 w-8 p-0">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center space-x-2">
                      <h2 className="text-xl font-bold text-gray-800">{formatDateRange(currentWeekStart)}</h2>
                      <select
                        className="px-2 py-1 pr-8 text-xs border rounded-md shadow-sm bg-white"
                        value={calendarView}
                        onChange={(e) => setCalendarView(e.target.value)}
                      >
                        <option value="week">Week</option>
                        <option value="biweek">2 Weeks</option>
                        <option value="month">Month</option>
                        <option value="twomonth">2 Months</option>
                        <option value="threemonth">3 Months</option>
                        <option value="sixmonth">6 Months</option>
                      </select>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigateWeek("next")} className="shadow-sm h-8 w-8 p-0">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button variant="outline" size="sm" onClick={toggleFullscreen} className="shadow-sm h-8 w-8 p-0">
                    {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex justify-between items-end">
                <div className="flex items-center text-sm flex-wrap gap-2">
                  <div className="flex items-center text-green-700">
                    <Plane className="h-3 w-3 mr-1" />
                    <span>Travel</span>
                  </div>
                  <div className="flex items-center text-yellow-700">
                    <HardHat className="h-3 w-3 mr-1" />
                    <span>Work permit</span>
                  </div>
                  <div className="flex items-center text-blue-700">
                    <Book className="h-3 w-3 mr-1" />
                    <span>Training</span>
                  </div>
                  <div className="flex items-center text-green-700">
                    <Move className="h-3 w-3 mr-1" />
                    <span>Drag to move the trip</span>
                  </div>
                  <div className="flex items-center text-green-700">
                    <Copy className="h-3 w-3 mr-1" />
                    <span>Copy travel</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTrainingWithoutTravel}
                      onChange={(e) => setShowTrainingWithoutTravel(e.target.checked)}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">Training without travel</span>
                  </label>
                  <div className="w-96 flex items-center bg-white rounded-full border shadow-sm p-2">
                    <Search className="h-5 w-5 text-gray-400 ml-4 mr-2" />
                    <input
                      type="text"
                      placeholder="Search employee (first name, last name)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full p-1 text-sm rounded border-none focus:ring-0 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-0">
            {loading ? (
              <div className="py-8 flex flex-col items-center justify-center">
                <p className="text-gray-600 text-sm">Ładowanie kalendarza podróży...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="max-h-[calc(100vh-120px)] overflow-y-auto">
                  <div
                    className="inline-grid"
                    style={{
                      gridTemplateColumns: `180px repeat(${getDaysForView().length}, ${getColumnWidth()})`,
                      minWidth: getGridMinWidth(),
                    }}
                  >
                    <div className="sticky top-0 left-0 z-30 bg-gray-100 font-semibold border-b">
                      <div className="w-full h-full p-2 ml-2 flex items-center text-xs font-medium text-gray-700">
                        Users ({getActiveUsers().length})
                      </div>
                    </div>
                    {getDaysForView().map((day, index) => {
                      const isToday = day.toDateString() === new Date().toDateString();
                      const isWeekend = day.getDay() === 0; //|| day.getDay() === 6//
                      return (
                        <div
                          key={index}
                          className={`sticky top-0 z-20 bg-gray-100 p-1 border-b ${isWeekend ? "bg-red-50" : isToday ? "bg-green-50 border border-green-400" : ""}`}
                        >
                          {formatDayDisplay(day, index)}
                        </div>
                      );
                    })}
                    {getActiveUsers().map((person) => (
                      <React.Fragment key={person.id}>
                        
                        <div
                          className="sticky left-0 z-10 bg-white p-2 hover:bg-gray-50 border-b border-r cursor-pointer"
                          onDoubleClick={() => {
                            console.log(`Clicked user ID: ${person.id}`);
                            setEditingUserId(person.id);
                            setIsUserFormOpen(true);
                          }}
                        >
                          <div className="w-full h-full cursor-pointer flex items-center justify-between">
                            <div className="ml-2 font-medium text-xs text-gray-900 truncate">{person.first_name} {person.last_name}</div>
                           
                          </div>
                        </div>
                        {getDaysForView().map((day, dayIndex) => (
                          <TravelCalendarCell
                            key={dayIndex}
                            person={person}
                            day={day}
                            travelEvents={travelEvents}
                            workPermits={workPermits}
                            trainings={trainings}
                            onDayClick={handleDayClick}
                            onOrderDoubleClick={handleOrderDoubleClick}
                            onEditTravel={(item) => handleEditTravel(person, day, item)}
                            onMoveTravel={handleMoveTravel}
                            onCopyTravel={handleCopyTravel}
                            calendarView={calendarView}
                          />
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          {selectedUserDay && (
            <UserDayTravelView
              user={selectedUserDay.person}
              date={selectedUserDay.date}
              travelEvents={travelEvents}
              workPermits={workPermits}
              trainings={trainings}
              companyId={companyId}
              onClose={() => setSelectedUserDay(null)}
              fetchData={fetchData}
            />
          )}
          {editingTravel && (
            <EditTravelDialog
              isOpen={!!editingTravel}
              onClose={() => setEditingTravel(null)}
              travelData={editingTravel.item}
              fetchData={fetchData}
              users={users}
            />
          )}
          {copyingTravel && (
            <CopyTravelDialog
              isOpen={!!copyingTravel}
              onClose={() => setCopyingTravel(null)}
              travelData={copyingTravel}
              users={users}
              fetchData={fetchData}
            />
          )}
          {dragIndicator && (
            <div className="fixed inset-0 bg-black bg-opacity-20 z-50 flex items-center justify-center pointer-events-none">
              <div className="bg-green-100 border border-green-500 p-4 rounded-lg shadow-lg">
                <p className="text-green-800 flex items-center">
                  <Move className="h-5 w-5 mr-2 animate-pulse" />
                  Przenoszenie podróży...
                </p>
              </div>
            </div>
          )}
          <AddOrEditUserForm
            isCreatorOpen={isUserFormOpen}
            setIsCreatorOpen={setIsUserFormOpen}
            editingUserId={editingUserId}
            onSave={handleUserSave}
          />
        </div>
      </DndProvider>
    </div>
  );
};

export default TravelPanel;